/*
This replaces the global fetch with a custom one that adds dataLayer events.
Also sets the baseURL so that server side requests are made to the varnish proxy.
(Sentry breadcrumbs are now handled automatically by @sentry/nuxt)
 */
import { defineNuxtPlugin } from 'nuxt/app'
import type { FetchOptions } from 'ofetch'
import { getAuthService } from '../apps/auth/service'
import { useAppStore } from '../stores/useAppStore'
import { logger } from '../utility/logger'

const isDev = process.env.NODE_ENV !== 'production'

// Custom interface for extended fetch options with retry attempt
interface CustomFetchOptions extends FetchOptions {
  retryAttempt?: number
}

// example of request with dataLayer is campsite-detail
function handleDataLayer(data: any, nuxtApp: any) {
  // Try multiple ways to access GTM
  const $gtm = nuxtApp.$gtm || nuxtApp.vueApp?.config?.globalProperties?.$gtm

  if (data && data.dataLayer && $gtm) {
    data.dataLayer.forEach((d: any) => $gtm.push(d))
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  globalThis.$fetch = $fetch.create({
    retry: process.server ? 0 : 3,
    retryDelay: (context) => {
      // Check if this is an abort error - if so, don't retry at all
      const error = context.error
      const isAbortError =
        error?.name === 'AbortError' ||
        error?.message?.includes('aborted') ||
        error?.message?.includes('AbortError')

      if (isAbortError) {
        if (isDev) {
          console.debug(
            '[ofetch] Abort error detected in retryDelay, preventing retry:',
            {
              errorName: error?.name,
              errorMessage: error?.message,
            },
          )
        }
        // Return 0 delay but set retry to 0 to prevent further attempts
        const options = context.options as any
        options.retry = 0
        return 0
      }

      // Use type assertion to avoid TypeScript errors
      const options = context.options as any
      const attempt = (options.retryAttempt = (options.retryAttempt || 0) + 1)
      // Calculate delay with exponential backoff and cap at 10 seconds
      const delay = Math.min(100 * Math.pow(2, attempt - 1), 10000)
      console.log('retryDelay', delay, context)
      return delay
    },
    timeout: 60000,
    baseURL: import.meta.client
      ? ''
      : process.env.NUXT_AXIOS_BASE_URL || 'http://varnish_plus:8100',
    onRequestError({ options, error }) {
      // More robust abort error detection
      const isAbortError =
        error.name === 'AbortError' ||
        error.message?.includes('aborted') ||
        error.message?.includes('AbortError') ||
        (error.name && error.name.toLowerCase().includes('abort'))

      if (isAbortError) {
        options.retry = 0
        // Don't process abort errors as network failures
        if (isDev) {
          console.debug('[ofetch] Request aborted, preventing retry:', {
            errorName: error.name,
            errorMessage: error.message,
            url:
              typeof options.request === 'string' ? options.request : 'unknown',
          })
        }
        return
      }

      // Only set network error for actual network issues (not aborts)
      if (
        import.meta.client &&
        ((options as CustomFetchOptions).retryAttempt || 0) >= 3
      ) {
        const appStore = useAppStore()
        appStore.setNetworkError(true)
      }
    },
    onRequest(ctx) {
      const { request, options } = ctx
      const url = typeof request === 'string' ? request : request.url
      // remove baseURL for api requests
      if (url.startsWith('/api/') || url.startsWith('/__')) {
        options.baseURL = undefined
      }

      // Check if this is a background fetch from Varnish or if gha2spon is in user agent
      const isBackgroundFetch =
        nuxtApp.ssrContext?.event.node.req.headers['x-background-fetch'] === '1'
      const incomingUserAgent =
        nuxtApp.ssrContext?.event.node.req.headers['user-agent'] || ''
      const hasGha2spon = incomingUserAgent.includes('gha2spon')

      if (isBackgroundFetch || hasGha2spon) {
        // Add gha2spon to user agent to force cache refresh
        const headers = options.headers || {}

        // Get current user agent if it exists
        let currentUserAgent = ''
        if (headers instanceof Headers) {
          currentUserAgent = headers.get('user-agent') || ''
        } else if (typeof headers === 'object') {
          const h = headers as Record<string, string>
          currentUserAgent = h['user-agent'] || h['User-Agent'] || ''
        }

        // Append gha2spon to existing user agent
        const finalUserAgent = currentUserAgent
          ? `${currentUserAgent} gha2spon`
          : 'gha2spon'

        // Set the new headers
        if (headers instanceof Headers) {
          headers.set('user-agent', finalUserAgent)
        } else {
          // Create object first, then assign property to avoid TypeScript union type issues
          const newHeaders = { ...(headers as Record<string, string>) }
          newHeaders['user-agent'] = finalUserAgent
          ;(options as any).headers = newHeaders
        }
      }

      if (url.startsWith('/__')) {
        // proxy the user-agent to the internal error pages
        const uaDevice =
          nuxtApp.ssrContext?.event.node.req.headers['x-ua-device']
        const headers = options.headers || {}
        options.headers = {
          ...headers,
          ...(uaDevice ? { 'x-ua-device': uaDevice as string } : {}),
        }
      }
      if (isDev || process.env.LOG_REQUESTS) {
        console.log('fetch request: ', url)
      }
    },
    onResponse({ request, response, options }) {
      const url = typeof request === 'string' ? request : request.url
      logger('fetch request response: ', url)

      if (import.meta.client) {
        // Reset network error state on successful response
        const appStore = useAppStore()
        if (appStore.hasNetworkError) {
          appStore.setNetworkError(false)
        }
      }
      handleDataLayer(response._data, nuxtApp)
    },
    onResponseError({ request, response, error }) {
      if (import.meta.client && error && error.name === 'AbortError') {
        if (isDev) {
          console.debug('[ofetch] Request aborted:', {
            reason: error.message,
            url: typeof request === 'string' ? request : request.url,
          })
        }
        // Don't process abort errors further - they're not network issues
        return
      }

      const url = typeof request === 'string' ? request : request.url
      const isUnauthorized = response.status === 401

      // Check if this is an expected 401 from the favourites endpoint during initial load
      // This is checked during hydration to see if user is logged in
      if (isUnauthorized) {
        const isFavouritesCheck = url.includes('/_/favourite-list')

        if (isFavouritesCheck) {
          // This is an expected 401 during initial page load - user just isn't logged in
          // Don't log to console, just handle the data layer and return silently
          handleDataLayer(response._data, nuxtApp)
          return
        }
      }

      const isAlfredRequest =
        request instanceof Request && request.headers.get('X-Alfred-Version')
      if (isAlfredRequest && isUnauthorized) {
        const authMachine = getAuthService()
        const isLoggedIn = authMachine.state.value === 'loggedIn'
        if (isLoggedIn) {
          authMachine.send('LOG_OUT')
        }
      }
      handleDataLayer(response._data, nuxtApp)
    },
  })
})
